import React, { useEffect, useMemo, useState, useCallback } from "react";
import { useBranch } from "@/contexts/BranchContext";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import {
  Plus, Trash2, ClipboardList, Save, AlertCircle, MessageSquare, X,
} from "lucide-react";
import {
  subActivityGrade, componentAverage, categoryGradeForStudent,
  periodGradeAvgCategories, finalGradeForStudent, formatGradeWithScale,
  directPercentEquivalent, directDisplay,
} from "@/lib/grading";

/* eslint-disable react-hooks/exhaustive-deps */

export default function GradesPage() {
  const { selectedSchool, selectedBranch, branchesForSelectedSchool, setSelectedBranchId } = useBranch();
  const branchId = selectedBranch?.id;

  // Scope
  const [classes, setClasses] = useState([]);
  const [sections, setSections] = useState([]);
  const [classId, setClassId] = useState("");
  const [sectionId, setSectionId] = useState("");
  const [students, setStudents] = useState([]);
  const [setup, setSetup] = useState(null); // resolved grading setup

  // Active selection
  const [periodId, setPeriodId] = useState("");
  const [categoryId, setCategoryId] = useState("");
  const [componentId, setComponentId] = useState("");

  const isOverallPeriod = periodId === "__overall__";

  // Data
  const [tasks, setTasks] = useState([]);
  const [scoresByTask, setScoresByTask] = useState({}); // taskId -> Map(studentId, score)
  const [directGrades, setDirectGrades] = useState({}); // key "level:target:student:period" -> {value, code}
  const [loading, setLoading] = useState(false);
  const [savingKey, setSavingKey] = useState("");

  // Load classes
  useEffect(() => {
    if (!branchId) { setClasses([]); return; }
    (async () => {
      try {
        const { data } = await api.get("/classes", { params: { branch_id: branchId } });
        setClasses((data || []).filter((c) => c.class_type === "regular" || c.class_type === "modular"));
      } catch (e) { toast.error(formatApiError(e)); }
    })();
  }, [branchId]);

  // Load sections
  useEffect(() => {
    setSectionId(""); setStudents([]);
    if (!classId || !branchId) { setSections([]); return; }
    (async () => {
      try {
        const { data } = await api.get("/sections", { params: { branch_id: branchId, class_id: classId } });
        setSections(data || []);
      } catch (e) { toast.error(formatApiError(e)); }
    })();
  }, [branchId, classId]);

  // Load students for the section
  useEffect(() => {
    if (!sectionId || !branchId) { setStudents([]); return; }
    (async () => {
      try {
        const { data } = await api.get("/students", { params: { branch_id: branchId, section_id: sectionId } });
        setStudents((data || []).filter((s) => s.status !== "inactive"));
      } catch (e) { toast.error(formatApiError(e)); }
    })();
  }, [branchId, sectionId]);

  // Load setup with fallback resolution
  useEffect(() => {
    if (!branchId) { setSetup(null); return; }
    setLoading(true);
    (async () => {
      try {
        const { data } = await api.get("/grading-setup", {
          params: {
            branch_id: branchId,
            class_id: classId || undefined,
            section_id: sectionId || undefined,
            resolve: true,
          },
        });
        setSetup(data);
        if (data.periods?.length && !data.periods.find((p) => p.id === periodId)) setPeriodId(data.periods[0].id);
        if (data.categories?.length && !data.categories.find((c) => c.id === categoryId)) setCategoryId(data.categories[0].id);
      } catch (e) { toast.error(formatApiError(e)); setSetup(null); }
      finally { setLoading(false); }
    })();
  }, [branchId, classId, sectionId]);

  const currentCategory = useMemo(
    () => setup?.categories.find((c) => c.id === categoryId) || null,
    [setup, categoryId],
  );
  // Pick first component when category changes
  useEffect(() => {
    if (!currentCategory) { setComponentId(""); return; }
    if (!currentCategory.components.find((c) => c.id === componentId)) {
      setComponentId(currentCategory.components[0]?.id || "");
    }
  }, [currentCategory]);

  const gradingMode = setup?.settings?.grading_mode || "calculated";
  const scaleType = setup?.settings?.grading_scale_type || "percentage";
  const directLevel = setup?.settings?.direct_assessment_level || "category";

  // Load tasks + scores (calculated mode)
  const loadTasks = useCallback(async () => {
    if (gradingMode !== "calculated" || !periodId || !categoryId || !componentId || !sectionId) {
      setTasks([]); setScoresByTask({}); return;
    }
    try {
      const { data: tlist } = await api.get("/grading/tasks", {
        params: {
          branch_id: branchId,
          grading_period_id: periodId,
          grade_category_id: categoryId,
          assessment_component_id: componentId,
          section_id: sectionId,
        },
      });
      setTasks(tlist || []);
      // Load scores in parallel for each task
      const byTask = {};
      await Promise.all((tlist || []).map(async (t) => {
        const { data: scs } = await api.get("/grading/scores", {
          params: { branch_id: branchId, assessment_task_id: t.id },
        });
        const m = new Map();
        for (const s of scs || []) m.set(s.student_id, s.score);
        byTask[t.id] = m;
      }));
      setScoresByTask(byTask);
    } catch (e) { toast.error(formatApiError(e)); }
  }, [branchId, periodId, categoryId, componentId, sectionId, gradingMode]);

  // Load direct grades (direct mode). Fetches BOTH the currently-selected
  // period's grades AND the sentinel "__overall__" entries so the teacher can
  // manually set an Overall grade when custom letter scales lack a numeric
  // equivalent (average can't be auto-computed).
  const loadDirect = useCallback(async () => {
    if (gradingMode !== "direct" || !periodId || !sectionId) { setDirectGrades({}); return; }
    try {
      const [periodRes, overallRes] = await Promise.all([
        api.get("/grading/direct-grades", {
          params: { branch_id: branchId, grading_period_id: periodId, level: directLevel },
        }),
        api.get("/grading/direct-grades", {
          params: { branch_id: branchId, grading_period_id: "__overall__", level: directLevel },
        }),
      ]);
      const out = {};
      for (const d of [...(periodRes.data || []), ...(overallRes.data || [])]) {
        out[`${d.level}:${d.target_id}:${d.student_id}:${d.grading_period_id}`] = {
          value: d.direct_grade_value, code: d.direct_grade_code,
        };
      }
      setDirectGrades(out);
    } catch (e) { toast.error(formatApiError(e)); }
  }, [branchId, periodId, sectionId, gradingMode, directLevel]);

  useEffect(() => { loadTasks(); }, [loadTasks]);
  useEffect(() => { loadDirect(); }, [loadDirect]);

  /* ------- Term comments ------- */
  const [comments, setComments] = useState({}); // {studentId: comment}
  const [commentStudent, setCommentStudent] = useState(null);
  const [commentDraft, setCommentDraft] = useState("");
  const [commentSaving, setCommentSaving] = useState(false);

  const loadComments = useCallback(async () => {
    if (!periodId || !branchId || !setup?.settings?.allow_grading_term_comments) {
      setComments({}); return;
    }
    try {
      const { data } = await api.get("/grading/term-comments", {
        params: { branch_id: branchId, grading_period_id: periodId },
      });
      const out = {};
      for (const c of data || []) out[c.student_id] = c.comment;
      setComments(out);
    } catch (e) { toast.error(formatApiError(e)); }
  }, [branchId, periodId, setup?.settings?.allow_grading_term_comments]);
  useEffect(() => { loadComments(); }, [loadComments]);

  const openComment = (stu) => {
    setCommentStudent(stu);
    setCommentDraft(comments[stu.id] || "");
  };
  const saveComment = async () => {
    if (!commentStudent) return;
    setCommentSaving(true);
    try {
      await api.put("/grading/term-comments", {
        branch_id: branchId, student_id: commentStudent.id,
        grading_period_id: periodId, comment: commentDraft,
      });
      setComments((prev) => {
        const next = { ...prev };
        if (commentDraft.trim()) next[commentStudent.id] = commentDraft;
        else delete next[commentStudent.id];
        return next;
      });
      setCommentStudent(null);
      toast.success(commentDraft.trim() ? "Comment saved" : "Comment cleared");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setCommentSaving(false); }
  };

  /* ------- Task CRUD ------- */
  const [newTaskName, setNewTaskName] = useState("");
  const [newTaskItems, setNewTaskItems] = useState(10);
  const createTask = async () => {
    if (!newTaskName.trim()) { toast.error("Task name required"); return; }
    try {
      await api.post("/grading/tasks", {
        branch_id: branchId, grading_period_id: periodId,
        grade_category_id: categoryId, assessment_component_id: componentId,
        section_id: sectionId, task_name: newTaskName, item_count: Number(newTaskItems) || null,
        scope_type: "class_section", sort_order: tasks.length,
      });
      setNewTaskName(""); setNewTaskItems(10);
      loadTasks();
      toast.success("Task created");
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const deleteTask = async (taskId) => {
    if (!window.confirm("Delete this task and all its scores?")) return;
    try { await api.delete(`/grading/tasks/${taskId}`); loadTasks(); toast.success("Task deleted"); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  /* ------- Score entry ------- */
  const saveScore = async (taskId, studentId, score) => {
    setSavingKey(`${taskId}:${studentId}`);
    try {
      await api.put("/grading/scores", {
        branch_id: branchId, student_id: studentId,
        assessment_task_id: taskId, score: Number(score) || 0,
      });
      // Update local cache
      setScoresByTask((prev) => {
        const m = new Map(prev[taskId] || []);
        m.set(studentId, Number(score) || 0);
        return { ...prev, [taskId]: m };
      });
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSavingKey(""); }
  };

  const saveDirectGrade = async (level, targetId, studentId, value, code, periodOverride = null) => {
    const pId = periodOverride || periodId;
    setSavingKey(`d:${level}:${targetId}:${studentId}:${pId}`);
    try {
      await api.put("/grading/direct-grades", {
        branch_id: branchId, level, target_id: targetId,
        student_id: studentId, grading_period_id: pId,
        direct_grade_value: value, direct_grade_code: code,
      });
      setDirectGrades((prev) => ({
        ...prev,
        [`${level}:${targetId}:${studentId}:${pId}`]: { value, code },
      }));
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSavingKey(""); }
  };

  /* ------- Summary stats ------- */
  const studentSummary = (student) => {
    if (gradingMode === "direct") {
      // Average of direct grade percentage equivalents across periods
      const perPeriod = (setup?.periods || []).map((p) => {
        const k = `${directLevel}:${categoryId}:${student.id}:${p.id}`;
        const d = directGrades[k];
        if (!d) return { period: p, grade: null };
        const pct = directPercentEquivalent(d.value, d.code, scaleType, setup.scale_ranges, setup.settings.custom_percentage_range_enabled);
        return { period: p, grade: pct };
      });
      return finalGradeForStudent(perPeriod.map((x) => ({ grade: x.grade, period_percentage: x.period.period_percentage })));
    }
    // Calculated mode: compute the category grade for this period
    if (!currentCategory) return null;
    const scoreByTask = new Map();
    for (const tid in scoresByTask) {
      const m = scoresByTask[tid];
      if (m.has(student.id)) scoreByTask.set(tid, m.get(student.id));
    }
    const tasksByComp = {};
    for (const t of tasks) (tasksByComp[t.assessment_component_id] ||= []).push(t);
    return categoryGradeForStudent(currentCategory.components, tasksByComp, scoreByTask);
  };

  const renderHeader = () => (
    <div className="mb-5">
      <h1 className="font-heading text-3xl sm:text-4xl font-semibold text-foreground tracking-tight">Grades</h1>
      <p className="text-brand-inkMuted mt-0.5 text-sm">Enter scores per task ({gradingMode === "calculated" ? "calculated mode" : `direct mode · ${directLevel} level`}). Configure terms / subjects / components in Grading Setup.</p>
    </div>
  );

  if (!branchId) return <>{renderHeader()}<p className="text-brand-inkMuted">Select a branch.</p></>;

  return (
    <>
      {renderHeader()}

      {/* Filters */}
      <section className="bg-white rounded-2xl border border-brand-purple/10 shadow-sm p-3 sm:p-4 mb-4" data-testid="grades-filters">
        <div className="grid sm:grid-cols-4 gap-3">
          <Field label="BRANCH">
            <select value={branchId} onChange={(e) => setSelectedBranchId(e.target.value)} className="g-select" data-testid="grades-branch">
              {branchesForSelectedSchool.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
            </select>
          </Field>
          <Field label="CLASS / LEVEL">
            <select value={classId} onChange={(e) => setClassId(e.target.value)} className="g-select" data-testid="grades-class">
              <option value="">— Select class —</option>
              {classes.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </Field>
          <Field label="SECTION">
            <select value={sectionId} onChange={(e) => setSectionId(e.target.value)} disabled={!classId} className="g-select" data-testid="grades-section">
              <option value="">— Select section —</option>
              {sections.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
            </select>
          </Field>
          <Field label="GRADING PERIOD">
            <select value={periodId} onChange={(e) => setPeriodId(e.target.value)} className="g-select" data-testid="grades-period">
              {setup?.periods?.map((p) => <option key={p.id} value={p.id}>{p.period_name}</option>)}
              {gradingMode === "direct" && setup?.periods?.length ? (
                <option value="__overall__">Overall (manual average)</option>
              ) : null}
            </select>
          </Field>
        </div>
        {setup && (
          <div className="mt-3 grid sm:grid-cols-2 gap-3">
            <Field label="SUBJECT / CATEGORY">
              <select value={categoryId} onChange={(e) => setCategoryId(e.target.value)} className="g-select" data-testid="grades-category">
                {setup.categories?.map((c) => <option key={c.id} value={c.id}>{c.category_name}</option>)}
              </select>
            </Field>
            {gradingMode === "calculated" && (
              <Field label="COMPONENT">
                <select value={componentId} onChange={(e) => setComponentId(e.target.value)} className="g-select" data-testid="grades-component">
                  {currentCategory?.components?.map((c) => <option key={c.id} value={c.id}>{c.component_name} ({c.component_percentage}%)</option>)}
                </select>
              </Field>
            )}
          </div>
        )}
      </section>

      {loading ? <p className="text-brand-inkMuted p-6">Loading...</p>
        : !setup?.periods?.length ? (
          <EmptyState title="No grading setup yet"
            subtitle="Configure grading periods and subjects in Grading Setup first, then come back here to enter scores."
          />
        ) : !sectionId ? (
          <EmptyState title="Pick a section" subtitle="Choose a class and section to load students." />
        ) : students.length === 0 ? (
          <EmptyState title="No students in this section" subtitle="Add students to this section from the Student List page." />
        ) : (
          <>
            {/* Standalone Term Comments panel — visible in either mode when
                "Allow Comments per Grading Period" is enabled and a real
                period is selected. Independent of subject/component/tasks so
                teachers can leave comments even before any tasks are set up. */}
            {setup.settings.allow_grading_term_comments && !isOverallPeriod && periodId && (
              <section className="bg-white rounded-2xl border border-brand-purple/10 shadow-sm p-4 mb-4" data-testid="grades-term-comments-panel">
                <div className="flex items-center gap-2 mb-3">
                  <MessageSquare className="w-4 h-4 text-brand-purple" />
                  <p className="font-heading font-semibold text-foreground">Teacher Comments — {setup.periods.find((p) => p.id === periodId)?.period_name}</p>
                </div>
                <div className="grid sm:grid-cols-2 gap-2">
                  {students.map((stu) => (
                    <div key={stu.id} className="flex items-center justify-between border border-brand-purple/10 rounded-xl px-3 py-2" data-testid={`grades-term-comment-row-${stu.id}`}>
                      <span className="text-sm text-foreground truncate mr-3">{stu.full_name || `${stu.name} ${stu.last_name}`.trim()}</span>
                      <CommentChip
                        has={!!comments?.[stu.id]}
                        onClick={() => openComment(stu)}
                        testId={`grades-term-comment-btn-${stu.id}`}
                      />
                    </div>
                  ))}
                </div>
                <p className="text-xs text-brand-inkMuted mt-2">Comments show at the bottom of the Report Card and printed report.</p>
              </section>
            )}
            {gradingMode === "direct" ? (
              <>
                {isOverallPeriod && (
                  <div className="mb-3 bg-brand-purpleLight/40 border border-brand-purple/20 rounded-xl px-4 py-2 text-sm text-foreground" data-testid="grades-overall-hint">
                    <strong className="font-heading">Overall grade mode:</strong> anything you enter here is saved as the manual average shown in the Average column of the Report Card. Use this when your custom scale has letter grades only.
                  </div>
                )}
                <DirectGradeTable
                  students={students}
                  scaleType={scaleType}
                  scaleRanges={setup.scale_ranges}
                  currentCategory={currentCategory}
                  categoryId={categoryId}
                  componentId={componentId}
                  directLevel={directLevel}
                  periodId={periodId}
                  tasks={tasks}
                  directGrades={directGrades}
                  onSave={saveDirectGrade}
                  savingKey={savingKey}
                  customDescEnabled={setup.settings.custom_description_enabled}
                  allowComments={setup.settings.allow_grading_term_comments}
                  comments={comments}
                  onOpenComment={openComment}
                />
              </>
            ) : !categoryId || !componentId ? (
              <EmptyState title="Pick a subject and component" subtitle="Choose where to enter scores." />
            ) : (
              <>
                {/* Add task panel */}
                <section className="bg-white rounded-2xl border border-brand-purple/10 shadow-sm p-4 mb-4">
                  <p className="font-heading font-semibold text-foreground mb-2">Tasks under {currentCategory?.category_name} · {currentCategory?.components.find((c) => c.id === componentId)?.component_name}</p>
                  <div className="flex flex-col sm:flex-row gap-2 items-end">
                    <Field label="TASK NAME" className="flex-1">
                      <Input value={newTaskName} onChange={(e) => setNewTaskName(e.target.value)}
                        placeholder='e.g. "Quiz #1: Verbs"' className="rounded-xl h-11" data-testid="grades-new-task-name" />
                    </Field>
                    <Field label="ITEMS">
                      <Input type="number" min={1} value={newTaskItems}
                        onChange={(e) => setNewTaskItems(Number(e.target.value))}
                        className="rounded-xl h-11 w-28" data-testid="grades-new-task-items" />
                    </Field>
                    <Button onClick={createTask} className="rounded-2xl h-11 bg-brand-purple hover:bg-brand-purpleHover text-white" data-testid="grades-add-task">
                      <Plus className="w-4 h-4 mr-1.5" /> Add Task
                    </Button>
                  </div>
                </section>

                {tasks.length === 0 ? (
                  <EmptyState title="No tasks yet" subtitle="Add a quiz, exam, or activity above to start entering scores." />
                ) : (
                  <ScoreMatrix
                    students={students}
                    tasks={tasks}
                    scoresByTask={scoresByTask}
                    onSave={saveScore}
                    savingKey={savingKey}
                    onDeleteTask={deleteTask}
                    scaleType={scaleType}
                    scaleRanges={setup.scale_ranges}
                    studentSummary={studentSummary}
                    allowComments={setup.settings.allow_grading_term_comments}
                    comments={comments}
                    onOpenComment={openComment}
                  />
                )}
              </>
            )}
          </>
        )
      }

      {/* Comment dialog */}
      {commentStudent && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={() => setCommentStudent(null)} data-testid="grades-comment-dialog">
          <div className="bg-white rounded-3xl max-w-lg w-full shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="p-5 border-b border-brand-purple/10 flex items-start justify-between">
              <div>
                <p className="text-[11px] uppercase tracking-wider text-brand-purple font-heading font-bold">Teacher Comment</p>
                <h2 className="font-heading text-xl font-bold text-foreground">{commentStudent.full_name || `${commentStudent.name} ${commentStudent.last_name}`.trim()}</h2>
                <p className="text-xs text-brand-inkMuted">{setup?.periods?.find((p) => p.id === periodId)?.period_name}</p>
              </div>
            </div>
            <div className="p-5">
              <textarea
                value={commentDraft}
                onChange={(e) => setCommentDraft(e.target.value)}
                placeholder="Write a narrative comment for this student this period..."
                rows={6}
                className="w-full rounded-xl border border-brand-purple/20 p-3 text-sm focus:outline-none focus:ring-2 focus:ring-brand-purple/40"
                data-testid="grades-comment-textarea"
              />
              <p className="text-xs text-brand-inkMuted mt-1">Leave empty and save to clear the comment.</p>
            </div>
            <div className="p-5 border-t border-brand-purple/10 flex justify-end gap-2">
              <Button variant="outline" onClick={() => setCommentStudent(null)} className="rounded-2xl">Cancel</Button>
              <Button onClick={saveComment} disabled={commentSaving}
                className="rounded-2xl bg-brand-purple hover:bg-brand-purpleHover text-white" data-testid="grades-comment-save">
                {commentSaving ? "Saving..." : "Save Comment"}
              </Button>
            </div>
          </div>
        </div>
      )}

      <style>{`
        .g-select {
          width: 100%; height: 2.75rem; border-radius: 0.75rem;
          border: 1px solid rgba(37, 99, 235, 0.2); background: #fff;
          padding: 0 0.75rem; font-size: 0.9rem; color: hsl(var(--foreground));
        }
      `}</style>
    </>
  );
}

/* ----------------- Sub-components ----------------- */

function Field({ label, children, className = "" }) {
  return (
    <div className={className}>
      <p className="text-[11px] uppercase tracking-wider text-brand-inkMuted font-heading">{label}</p>
      <div className="mt-1.5">{children}</div>
    </div>
  );
}

function EmptyState({ title, subtitle }) {
  return (
    <div className="bg-white rounded-2xl border border-brand-purple/10 shadow-sm p-12 text-center">
      <div className="w-14 h-14 mx-auto rounded-2xl bg-brand-purpleLight flex items-center justify-center mb-3">
        <ClipboardList className="w-7 h-7 text-brand-purple" />
      </div>
      <p className="font-heading text-lg font-semibold text-foreground">{title}</p>
      {subtitle && <p className="text-brand-inkMuted mt-1 text-sm max-w-md mx-auto">{subtitle}</p>}
    </div>
  );
}

function ScoreMatrix({ students, tasks, scoresByTask, onSave, savingKey, onDeleteTask, scaleType, scaleRanges, studentSummary, allowComments, comments, onOpenComment }) {
  return (
    <div className="bg-white rounded-2xl border border-brand-purple/10 shadow-sm overflow-x-auto" data-testid="grades-score-matrix">
      <table className="w-full text-sm">
        <thead>
          <tr className="bg-brand-purpleLight/40">
            <th className="text-left px-3 py-2 font-heading sticky left-0 bg-brand-purpleLight/40 min-w-[200px]">Student</th>
            {tasks.map((t) => (
              <th key={t.id} className="px-3 py-2 font-heading text-center min-w-[120px]">
                <div className="flex items-center justify-center gap-1">
                  <span>{t.task_name}</span>
                  <button onClick={() => onDeleteTask(t.id)} className="text-[#C62828] opacity-50 hover:opacity-100" title="Delete task" data-testid={`grades-del-task-${t.id}`}>
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
                <p className="text-xs text-brand-inkMuted font-normal">/{t.item_count || "—"}</p>
              </th>
            ))}
            <th className="px-3 py-2 font-heading text-center bg-brand-purpleLight/60 min-w-[120px]">Component Avg</th>
            {allowComments && <th className="px-3 py-2 font-heading text-center min-w-[80px]">Comment</th>}
          </tr>
        </thead>
        <tbody>
          {students.map((stu) => {
            return (
              <tr key={stu.id} className="border-t border-brand-purple/10" data-testid={`grades-row-${stu.id}`}>
                <td className="px-3 py-2 font-heading sticky left-0 bg-white">{stu.full_name || `${stu.name} ${stu.last_name}`.trim()}</td>
                {tasks.map((t) => {
                  const cur = scoresByTask[t.id]?.get(stu.id);
                  const sk = `${t.id}:${stu.id}`;
                  return (
                    <td key={t.id} className="px-2 py-2 text-center">
                      <input
                        type="number"
                        defaultValue={cur ?? ""}
                        onBlur={(e) => {
                          const v = e.target.value;
                          if (v === "" && cur == null) return;
                          if (v !== "" && Number(v) === cur) return;
                          onSave(t.id, stu.id, v === "" ? 0 : v);
                        }}
                        className="w-16 h-9 text-center border border-brand-purple/20 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-purple/40"
                        data-testid={`grades-score-${t.id}-${stu.id}`}
                      />
                      <p className="text-[10px] text-brand-inkMuted mt-0.5">
                        {cur != null && t.item_count ? `${subActivityGrade(cur, t.item_count)}%` : ""}
                        {savingKey === sk && " ..."}
                      </p>
                    </td>
                  );
                })}
                <td className="px-3 py-2 text-center font-heading font-semibold tabular-nums bg-brand-purpleLight/30">
                  {formatGradeWithScale(studentSummary(stu), scaleType, scaleRanges)}
                </td>
                {allowComments && (
                  <td className="px-2 py-2 text-center">
                    <CommentChip has={!!comments?.[stu.id]} onClick={() => onOpenComment(stu)} testId={`grades-comment-btn-${stu.id}`} />
                  </td>
                )}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function CommentChip({ has, onClick, testId }) {
  return (
    <button onClick={onClick} data-testid={testId}
      className={`inline-flex items-center gap-1 text-xs px-2.5 py-1 rounded-full border ${has
        ? "border-brand-purple bg-brand-purpleLight text-brand-purple hover:bg-brand-purple hover:text-white"
        : "border-dashed border-brand-purple/30 text-brand-inkMuted hover:bg-brand-purpleLight/40"} transition`}
      title={has ? "Edit comment" : "Add comment"}
    >
      <MessageSquare className="w-3.5 h-3.5" />
      {has ? "Edit" : "Add"}
    </button>
  );
}

function DirectGradeTable({
  students, scaleType, scaleRanges, currentCategory, categoryId, componentId,
  directLevel, periodId, tasks, directGrades, onSave, savingKey, customDescEnabled,
  allowComments, comments, onOpenComment,
}) {
  // For direct mode, render one column per target depending on directLevel.
  const targets = useMemo(() => {
    if (directLevel === "category") return [{ id: categoryId, name: currentCategory?.category_name || "Subject" }];
    if (directLevel === "component") return (currentCategory?.components || []).map((c) => ({ id: c.id, name: c.component_name }));
    // task — not fully implemented (would need tasks list)
    return tasks.map((t) => ({ id: t.id, name: t.task_name }));
  }, [directLevel, categoryId, currentCategory, tasks]);

  if (!targets.length) {
    if (directLevel === "task") {
      return <EmptyState
        title="No tasks defined yet"
        subtitle="Task-level direct grading needs tasks to fill in. Switch Direct-Assessment level to 'Category' or 'Component' in Grading Setup, or add tasks under this subject first."
      />;
    }
    return <EmptyState title="Nothing to grade yet" subtitle="Pick a subject/component first." />;
  }

  return (
    <div className="bg-white rounded-2xl border border-brand-purple/10 shadow-sm overflow-x-auto" data-testid="grades-direct-table">
      <table className="w-full text-sm">
        <thead>
          <tr className="bg-brand-purpleLight/40">
            <th className="text-left px-3 py-2 font-heading min-w-[200px]">Student</th>
            {targets.map((t) => (
              <th key={t.id} className="px-3 py-2 font-heading text-center min-w-[160px]">{t.name}</th>
            ))}
            {allowComments && <th className="px-3 py-2 font-heading text-center min-w-[80px]">Comment</th>}
          </tr>
        </thead>
        <tbody>
          {students.map((stu) => (
            <tr key={stu.id} className="border-t border-brand-purple/10" data-testid={`grades-direct-row-${stu.id}`}>
              <td className="px-3 py-2 font-heading">{stu.full_name || `${stu.name} ${stu.last_name}`.trim()}</td>
              {targets.map((tgt) => {
                const k = `${directLevel}:${tgt.id}:${stu.id}:${periodId}`;
                const d = directGrades[k] || {};
                const sk = `d:${directLevel}:${tgt.id}:${stu.id}:${periodId}`;
                return (
                  <td key={tgt.id} className="px-2 py-2 text-center">
                    {scaleType === "percentage" ? (
                      <input type="number" min={0} max={100} step={0.01}
                        defaultValue={d.value ?? ""}
                        onBlur={(e) => {
                          const v = e.target.value;
                          if (v === "" && d.value == null) return;
                          onSave(directLevel, tgt.id, stu.id, v === "" ? null : Number(v), "");
                        }}
                        className="w-20 h-9 text-center border border-brand-purple/20 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-purple/40"
                        data-testid={`grades-direct-input-${tgt.id}-${stu.id}`}
                      />
                    ) : (
                      <select defaultValue={d.code || ""}
                        onChange={(e) => onSave(directLevel, tgt.id, stu.id, null, e.target.value || "")}
                        className="h-9 border border-brand-purple/20 rounded-lg px-2"
                        data-testid={`grades-direct-select-${tgt.id}-${stu.id}`}
                      >
                        <option value="">—</option>
                        {scaleRanges.map((r) => (
                          <option key={r.code_or_value} value={r.code_or_value}>{r.code_or_value}{customDescEnabled && r.description ? ` · ${r.description}` : ""}</option>
                        ))}
                      </select>
                    )}
                    {savingKey === sk && <p className="text-[10px] text-brand-inkMuted">Saving...</p>}
                  </td>
                );
              })}
              {allowComments && (
                <td className="px-2 py-2 text-center">
                  <CommentChip has={!!comments?.[stu.id]} onClick={() => onOpenComment(stu)} testId={`grades-comment-btn-${stu.id}`} />
                </td>
              )}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
